
import React from 'react';
import EnhancedSEOSystem from '@/components/seo/EnhancedSEOSystem';
import { useSEOData } from '@/hooks/useSEOData';
import { useLanguage } from '@/context/LanguageContext';

const Terms = () => {
  const { t } = useLanguage();

  const seoData = useSEOData({
    pageType: 'static',
    customData: {
      title: 'Terms of Service | Global Therapy Connect - Platform Usage Guidelines',
      description: 'Read our comprehensive terms of service to understand the rules, guidelines, and responsibilities for using our global therapy platform connecting patients with mental health professionals.',
      keywords: 'terms of service, therapy platform terms, user agreement, platform guidelines, therapy service rules, mental health platform terms, therapist platform agreement, user responsibilities',
      ogType: 'article',
      ogImage: 'https://psychotherapist.help/og-terms.png'
    }
  });

  return (
    <>
      <EnhancedSEOSystem
        pageData={seoData}
        pageType="static"
      />

      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-16 max-w-4xl">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-8">
              Last updated: January 2024
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Acceptance of Terms</h2>
              <p className="text-gray-700">
                By accessing and using Global Therapy Hub, you accept and agree to be bound by 
                these Terms of Service. If you do not agree to these terms, please do not use our platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Platform Description</h2>
              <p className="text-gray-700">
                Global Therapy Hub is a platform that connects clients with certified mental health 
                professionals for therapy sessions. We facilitate the connection but do not provide 
                medical advice or treatment directly.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">User Responsibilities</h2>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">For Clients:</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Provide accurate and complete information</li>
                <li>Attend scheduled sessions or cancel with appropriate notice</li>
                <li>Pay for services as agreed</li>
                <li>Respect therapist boundaries and professional guidelines</li>
                <li>Use the platform for legitimate therapeutic purposes only</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">For Therapists:</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Maintain current professional licenses and certifications</li>
                <li>Complete required training programs</li>
                <li>Provide professional and ethical treatment</li>
                <li>Maintain client confidentiality</li>
                <li>Follow platform guidelines and policies</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Terms</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Clients pay therapists directly through our secure payment system</li>
                <li>A 10% platform fee is charged on completed online sessions</li>
                <li>Refunds are subject to individual therapist policies</li>
                <li>All payments must be made through the platform</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Limitations of Liability</h2>
              <p className="text-gray-700">
                Global Therapy Hub is a platform facilitating connections between clients and 
                therapists. We are not responsible for the quality of therapy services, 
                treatment outcomes, or any issues arising from the therapeutic relationship.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Termination</h2>
              <p className="text-gray-700">
                We reserve the right to terminate or suspend accounts that violate these terms 
                or engage in inappropriate behavior on the platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
              <p className="text-gray-700">
                For questions about these Terms of Service, contact us at:
              </p>
              <p className="text-gray-700 mt-4">
                Email: legal@psychotherapist.help<br />
                Address: Global Therapy Hub Legal Team
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default Terms;
